import { inject } from "@angular/core";
import { type ActivatedRouteSnapshot, type CanActivateFn, Router } from "@angular/router";

import { map, type Observable, of } from "rxjs";

import { LINKS } from "@/app/shared/constants";
import { AuthService } from "../services/auth.service";
import { hasGrantedRole } from "./role-hierarchy";

/**
 * Guard that restricts route access based on user roles.
 *
 * This guard checks the current route path against a predefined list of
 * allowed roles from the application’s link configuration. If the user
 * has at least one matching role (directly or via the ADMIN super-role
 * hierarchy), access is granted. Otherwise, the user is redirected to
 * the 403 (Forbidden) page.
 *
 * @param {ActivatedRouteSnapshot} route - The current route snapshot being accessed.
 * @returns {Observable<boolean>} An observable that emits `true` if the user
 * has permission to access the route and `false` otherwise.
 */
export const RoleGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const path = route.routeConfig?.path;

  const allowedRoles = getAllowedRolesForPath(path ?? "");
  if (!allowedRoles) return of(true);

  return auth.loadUser().pipe(
    map((user) => {
      const valid = allowedRoles.some((r) => hasGrantedRole(user.roles, r));
      if (valid) {
        return true;
      }
      router.parseUrl("/403");
      return false;
    }),
  );
};

/**
 * Retrieves the list of roles allowed to access a given route path.
 *
 * It searches the application's link configuration (LINKS) for a route whose
 * URL matches the provided path, returning its allowed roles if defined.
 *
 * @param {string} path - The route path to check.
 * @returns {string[] | undefined} The list of allowed roles for the given path,
 * or `undefined` if no specific restrictions exist.
 */
function getAllowedRolesForPath(path: string): string[] | undefined {
  for (const link of LINKS) {
    if (link.url === path && link.allowedRoles) {
      return link.allowedRoles;
    }

    if (link.items) {
      const match = link.items.find((item) => item.url === path);
      if (match?.allowedRoles) return match.allowedRoles;
    }
  }

  return undefined;
}
